import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { Reveal } from "@/components/ui-ext/Reveal";
import { Picture } from "@/components/media/Picture";
import { siteConfig } from "@/config/site";

const SURFACES = [
  { name: "Google AI Overviews", pct: "38%", d: "of informational queries now show an AI Overview above the classic blue links." },
  { name: "ChatGPT Search", pct: "24%", d: "of B2B research sessions now originate on ChatGPT rather than a search engine." },
  { name: "Perplexity", pct: "14%", d: "answer-first traffic converts 3× the rate of position-10 organic clicks." },
  { name: "Gemini · Copilot", pct: "9%", d: "of enterprise queries route through an assistant before hitting a browser." },
];

export function HomeAISearch() {
  const city = siteConfig.contact.address.city;
  const state = siteConfig.contact.address.state;

  return (
    <section className="container-page py-16 md:py-24">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
        <div>
          <SectionHeading
            eyebrow={`AEO · GEO · ${city} AI search`}
            title={<>The <span className="text-gradient">answer engine</span> is {city}'s new front page.</>}
            intro={`Ranking #1 on Google isn't enough anymore. When ${city} buyers ask ChatGPT, Perplexity, Gemini or Google AI Overviews "best HVAC company near me" or "top SEO agency in ${city}, ${state}" — and your brand isn't cited — you don't exist in the moment the buying decision happens. ${siteConfig.name} engineers for both classic search and generative engines.`}
          />
          <div className="mt-8 space-y-4 text-sm text-muted-foreground leading-relaxed max-w-lg">
            <p>
              We treat every commercial-intent and {city} service-area page as a dual asset: <span className="text-foreground">indexable for Google, extractable for LLMs.</span> That means citation-friendly structure, unambiguous entity signaling (business, neighborhood, service, review, price), dense first-party evidence, and machine-legible schema (LocalBusiness, Service, FAQ, Article).
            </p>
            <p>
              Our AI Search Readiness Scorecard grades every priority page on the four inputs generative engines actually reward — and we run the same scorecard against your top three {city} competitors so we know exactly what to close, in what order.
            </p>
          </div>
          <div className="mt-10 rounded-2xl overflow-hidden border border-border bg-surface-2">
            <Picture
              variantKey="ai-search"
              alt={`ChatGPT and Google AI Overviews citing an ${city} SEO agency`}
              width={1408}
              height={1008}
              sizes="(min-width: 1024px) 900px, 100vw"
            />
          </div>
        </div>
        <div className="grid gap-4">
          {SURFACES.map((s, i) => (
            <Reveal key={s.name} delay={(i % 4) as 0 | 1 | 2 | 3}>
              <article className="surface-card p-6 relative overflow-hidden">
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-display text-lg">{s.name}</h3>
                  <div className="font-display text-3xl text-gradient">{s.pct}</div>
                </div>
                <div
                  aria-hidden
                  className="mt-4 h-1.5 rounded-full bg-surface-2 overflow-hidden"
                >
                  <div
                    className="h-full rounded-full"
                    style={{ width: s.pct, background: "var(--gradient-primary)" }}
                  />
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  {s.d}
                </p>
              </article>
            </Reveal>
          ))}
          <div className="text-[0.7rem] text-muted-foreground">
            Directional industry benchmarks · category-varying · used for planning, not reporting.
          </div>
        </div>
      </div>
    </section>
  );
}
